import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import Layout, { Overline } from "../components/Layout";
import PageHero from "../components/PageHero";
import { Reveal } from "../components/Motion";
import { Marquee } from "../components/Marquee";
import { IMAGES, VALUES, TIMELINE } from "../data/site";

export default function About() {
  return (
    <Layout
      title="About"
      description="Ranger Base exists to strengthen Indigenous stewardship institutions — building the people, systems, knowledge and governance behind strong ranger programs around the world."
    >
      <PageHero
        overline="About Ranger Base"
        titleLines={["We build the people", "behind strong Country"]}
        subtitle="Ranger Base is not a consultancy that advises from a distance. We partner with Traditional Owner corporations to build the capability that stays."
        image={IMAGES.heroRangers}
      />

      {/* Story */}
      <section className="py-24 lg:py-36 bg-warmwhite">
        <div className="mx-auto max-w-[88rem] px-6 lg:px-10 grid lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          <div className="lg:col-span-5 lg:sticky lg:top-28">
            <div className="text-eucalyptus mb-6"><Overline>Our Story</Overline></div>
            <Reveal>
              <h2 className="font-heading text-4xl md:text-5xl font-semibold tracking-tighter text-charcoal leading-[0.95]">
                Strengthening institutions, not creating dependence.
              </h2>
            </Reveal>
          </div>
          <div className="lg:col-span-7 space-y-6 text-lg text-charcoal/75 leading-relaxed">
            <Reveal>
              <p>Ranger Base began with a simple belief: the people doing the real work on Country deserve practical, experienced support that helps them do it better — not generic advice handed down from afar.</p>
            </Reveal>
            <Reveal delay={0.08}>
              <p>Around the world, Indigenous and Traditional Owner organisations hold extraordinary cultural knowledge. Yet they are increasingly asked to manage carbon projects, monitor biodiversity, collect native seed, protect cultural heritage and satisfy complex funding and reporting systems — often without the systems or staff to match.</p>
            </Reveal>
            <Reveal delay={0.16}>
              <p>We work alongside these organisations for the years it takes to build strong ranger programs. We share skills, build systems and hand back the knowledge, data and documentation that keep working long after an engagement ends.</p>
            </Reveal>
            <Reveal delay={0.24}>
              <p>Our focus is capability, culture, Country and partnership — and the long-term stewardship that comes from getting all four right.</p>
            </Reveal>
            <Reveal delay={0.3}>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 pt-6">
                {[["Global", "Service delivery"], ["Multi-year", "Partnerships"], ["On-Country", "Practical work"], ["Community-led", "Cultural authority"]].map(([a, b]) => (
                  <div key={a} className="rounded-2xl bg-sand p-5">
                    <div className="font-heading text-2xl font-semibold tracking-tight text-eucalyptus">{a}</div>
                    <div className="text-sm text-charcoal/60 mt-1">{b}</div>
                  </div>
                ))}
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Image band */}
      <section className="relative h-[74vh] min-h-[560px] overflow-hidden">
        <motion.img
          initial={{ scale: 1.15 }} whileInView={{ scale: 1 }} viewport={{ once: true }} transition={{ duration: 1.6, ease: [0.22, 1, 0.36, 1] }}
          src={IMAGES.outbackWide} alt="Australian landscape" className="absolute inset-0 h-full w-full object-cover object-center"
        />
        <div className="absolute inset-0 bg-charcoal/30" />
        <div className="grain absolute inset-0 opacity-30" />
      </section>

      {/* Timeline */}
      <section className="py-24 lg:py-36 bg-eucalyptus-deep text-warmwhite">
        <div className="mx-auto max-w-[80rem] px-6 lg:px-10">
          <div className="text-sandstone mb-6"><Overline>Our Journey</Overline></div>
          <h2 className="font-heading text-4xl md:text-5xl font-semibold tracking-tighter leading-[0.95] max-w-2xl">A partnership measured in decades.</h2>
          <div className="mt-16 relative">
            <div className="absolute left-0 md:left-1/2 top-0 bottom-0 w-px bg-warmwhite/20" />
            <div className="space-y-12">
              {TIMELINE.map((t, i) => (
                <Reveal key={t.title} delay={0.05}>
                  <div className={`relative md:grid md:grid-cols-2 md:gap-16 items-center ${i % 2 ? "" : ""}`}>
                    <div className={`pl-8 md:pl-0 ${i % 2 ? "md:col-start-2" : "md:text-right md:pr-16"}`}>
                      <span className="font-mono text-sm text-ochre">{t.year}</span>
                      <h3 className="mt-2 font-heading text-2xl md:text-3xl font-semibold tracking-tight">{t.title}</h3>
                      <p className="mt-3 text-warmwhite/70 leading-relaxed">{t.desc}</p>
                    </div>
                    <span className="absolute left-[-5px] md:left-1/2 md:-translate-x-1/2 top-1.5 h-3 w-3 rounded-full bg-ochre ring-4 ring-eucalyptus-deep" />
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-24 lg:py-36 bg-warmwhite">
        <div className="mx-auto max-w-[88rem] px-6 lg:px-10">
          <div className="text-eucalyptus mb-6"><Overline>Our Values</Overline></div>
          <h2 className="font-heading text-4xl md:text-5xl lg:text-6xl font-semibold tracking-tighter text-charcoal leading-[0.95] max-w-3xl">What guides every step of the work.</h2>
          <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {VALUES.map((v, i) => (
              <Reveal key={v.title} delay={(i % 3) * 0.07}>
                <div className="group h-full rounded-3xl border border-charcoal/10 bg-white p-8 hover:-translate-y-1 transition-transform duration-300">
                  <span className="font-mono text-sm text-ochre">{String(i + 1).padStart(2, "0")}</span>
                  <h3 className="mt-4 font-heading text-2xl font-semibold tracking-tight text-charcoal">{v.title}</h3>
                  <p className="mt-3 text-charcoal/65 leading-relaxed">{v.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 lg:py-20 bg-sand border-y border-charcoal/10 text-eucalyptus">
        <Marquee items={["Country", "Culture", "Capability", "Partnership", "Stewardship"]} />
      </section>

      <section className="py-24 bg-warmwhite">
        <div className="mx-auto max-w-[80rem] px-6 lg:px-10 rounded-[2rem] bg-charcoal text-warmwhite p-10 lg:p-16 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-8">
          <h2 className="font-heading text-3xl md:text-4xl font-semibold tracking-tight max-w-xl">These are the people we want beside us over the next decade.</h2>
          <Link to="/contact" data-testid="about-cta" className="inline-flex items-center gap-2 rounded-full bg-ochre text-white px-7 py-4 font-semibold hover:bg-ochre/90 transition-colors shrink-0">
            Work With Us <ArrowUpRight size={18} />
          </Link>
        </div>
      </section>
    </Layout>
  );
}
